// The four pipeline stages, in order. Values match the CHECK constraints in
// backend/supabase/migrations/20261016180000_pipeline_stages_and_founders.sql.
export const STAGES = [
  { value: "idea", label: "Idea" },
  { value: "in_talks", label: "In Talks" },
  { value: "complete", label: "Complete" },
  { value: "payment_received", label: "Payment Received" },
] as const;

export type Stage = (typeof STAGES)[number]["value"];

export function isStage(v: unknown): v is Stage {
  return STAGES.some((s) => s.value === v);
}

export function stageLabel(v: string | null): string {
  return STAGES.find((s) => s.value === v)?.label ?? "—";
}

export function stageIndex(v: Stage): number {
  return STAGES.findIndex((s) => s.value === v);
}

const whole = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const cents = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** USD. Whole dollars without cents ("$12,500"); otherwise two places ("$12,500.50"). */
export function usd(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  return Number.isInteger(v) ? whole.format(v) : cents.format(v);
}

/** Sum of deal values; a missing value counts as $0. Summed in cents to avoid float drift. */
export function sumValues(rows: { deal_value: number | string | null }[]): number {
  return rows.reduce((cents, r) => cents + Math.round(Number(r.deal_value ?? 0) * 100), 0) / 100;
}

/** A typed amount ("12,500", "$1,200.50") -> number, or null for blank. Throws on anything else. */
export function parseUsd(text: string): number | null {
  const t = text.replace(/[$,\s]/g, "");
  if (t === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) throw new Error(`"${text}" is not a dollar amount`);
  return Number(t);
}

export type Totals = { revenue: number; pipeline: number; byStage: Record<Stage, { count: number; value: number }> };

/** Dashboard figures: revenue = Payment Received; pipeline = every other stage. */
export function totals(rows: { stage: Stage; deal_value: number | string | null }[]): Totals {
  const byStage = Object.fromEntries(STAGES.map((s) => [s.value, { count: 0, value: 0 }])) as Totals["byStage"];
  for (const s of STAGES) {
    const inStage = rows.filter((r) => r.stage === s.value);
    byStage[s.value] = { count: inStage.length, value: sumValues(inStage) };
  }
  return {
    revenue: byStage.payment_received.value,
    pipeline: sumValues(rows.filter((r) => r.stage !== "payment_received")),
    byStage,
  };
}
